/**
 * Protected Route Component
 *
 * Ensures user is authenticated before rendering children
 * Redirects to /login if not authenticated
 * Redirects organization members to /organization-paused if org is paused
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { loginUrlWithRedirect } from '@/utils/safeRedirect';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve where they were headed. This is what makes a push notification tapped by a
    // user whose session has lapsed still land on the right screen — the single most
    // common real case, since push exists to reach people who haven't opened the app.
    // Previously the destination was discarded here and everyone arrived at /dashboard,
    // while LoginPage's `?redirect=` handling sat unused.
    // Sanitized: the param is attacker-controllable, and `//evil.com` would otherwise
    // navigate off-site (see utils/safeRedirect.ts).
    return <Navigate to={loginUrlWithRedirect(location.pathname + location.search)} replace />;
  }

  // Check if user is an organization member with a paused organization
  // Don't redirect if already on the organization-paused page
  if (
    user?.organizationMembership &&
    user.organizationMembership.status === 'paused' &&
    user.organizationMembership.role !== 'admin' && // Owner handles paused org differently (via billing/plans)
    location.pathname !== '/organization-paused'
  ) {
    return <Navigate to="/organization-paused" replace />;
  }

  return <>{children}</>;
}
